/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { memo } from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Skeleton, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';

import { SearchResultsHeaderText } from 'bundles/search-common/components/SearchResultsHeaderText';
import { isInFilterTestVariant } from 'bundles/search-common/utils/experimentUtils';
import useIsConsumerSerp from 'bundles/search-common/utils/useIsConsumerSerp';

import _t from 'i18n!nls/search-common';

type Props = {
  searchQuery?: string;
  numberOfHits?: number;
  shouldHide?: boolean;
  activeFilters?: string[];
  isLoading?: boolean;
  activeFilterCount?: number;
  hitCountRounder?: (count: number) => number;
  lessenHeaderFocus?: boolean;
  hideHits?: boolean;
};

const styles = {
  header: css`
    margin-bottom: var(--cds-spacing-300);

    @media (min-width: ${breakpoints.values.sm}px) and (max-width: ${breakpoints.values.md - 1}px) {
      padding-left: var(--cds-spacing-50);
    }

    @media (min-width: ${breakpoints.values.md}px) {
      margin-top: var(--cds-spacing-100);
      margin-bottom: var(--cds-spacing-200);
    }

    @media (max-width: ${breakpoints.values.md - 1}px) {
      min-height: var(--cds-spacing-400);
    }
  `,
  headerNoMarginOnMobile: css`
    @media (max-width: ${breakpoints.values.md - 1}px) {
      margin-bottom: 0;
    }
  `,
  placeholderWrapper: css`
    padding-bottom: var(--cds-spacing-50);
    display: flex;
    flex-direction: column;

    .results-text-placeholder {
      position: relative;
      display: block;
      top: -2px;
      margin-bottom: 10px !important;
    }

    .filter-placeholder {
      padding-bottom: var(--cds-spacing-50);
      margin-top: 6px;
      display: flex;
      justify-content: left;

      .chip-placeholder {
        margin-right: var(--cds-spacing-200);
      }
    }
  `,
};

export const SearchResultsHeader: React.FC<Props> = (props: Props) => {
  const {
    searchQuery,
    numberOfHits,
    shouldHide,
    activeFilters,
    isLoading,
    activeFilterCount,
    hitCountRounder,
    lessenHeaderFocus = false,
    hideHits = false,
  } = props;

  const isMobile = useMediaQuery(breakpoints.down('sm'));
  const isConsumerSearchPage = useIsConsumerSerp();
  const inFilterTestVariantAndOnSearch = isInFilterTestVariant() && isConsumerSearchPage;
  const hideMarginOnMobile = isMobile && inFilterTestVariantAndOnSearch;

  if (shouldHide) {
    return null;
  }

  if (isLoading) {
    return (
      <div css={styles.placeholderWrapper}>
        <Skeleton className="results-text-placeholder" width="17em" height="46px" />
        {Boolean(activeFilterCount) && (
          <div className="filter-placeholder">
            {[
              Array.from({ length: Math.min(activeFilterCount ?? 0, 5) }).map((_, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <Skeleton className="chip-placeholder" width="8em" height="46px" key={i} />
              )),
            ]}
          </div>
        )}
      </div>
    );
  }

  const shouldShowNoResultsHeader = isLoading === false && numberOfHits === undefined;
  if (shouldShowNoResultsHeader) {
    return (
      <div className="rc-SearchResultsHeader">
        <Typography2
          variant={lessenHeaderFocus ? 'subtitleLarge' : 'titleSmall'}
          component="h1"
          role="heading"
          css={[styles.header, hideMarginOnMobile && styles.headerNoMarginOnMobile]}
        >
          <div data-e2e="NumberOfResultsSection">
            <FormattedMessage
              message={searchQuery ? _t('No results found for "{query}"') : _t('No results found for your search')}
              query={searchQuery}
            />
          </div>
        </Typography2>
      </div>
    );
  }

  return (
    <div className="rc-SearchResultsHeader">
      <Typography2
        variant={lessenHeaderFocus ? 'subtitleLarge' : 'titleSmall'}
        component="h2"
        role="heading"
        css={[styles.header, hideMarginOnMobile && styles.headerNoMarginOnMobile]}
      >
        <div data-e2e="NumberOfResultsSection">
          <SearchResultsHeaderText
            searchQuery={searchQuery}
            numberOfHits={numberOfHits}
            activeFilters={activeFilters}
            hitCountRounder={hitCountRounder}
            hideHits={hideHits}
          />
        </div>
      </Typography2>
    </div>
  );
};

export default memo(SearchResultsHeader);
